/**
 * Where the visitor's id lives.
 *
 * Its own module, with NO side effects on import, so it can be tested without
 * booting the widget. `index.ts` starts fetching the moment it is imported,
 * which made an earlier version of the test try to reach the network.
 */

import { isPlausibleTime } from "./time.js";

/**
 * No token is the DEFAULT, not an error state. A marketing site gets a working
 * assistant from a script tag alone, with read-only tools and history scoped to
 * this tab. That is what makes the five-minute claim true.
 *
 * 🔴 sessionStorage, NOT localStorage. Doc 18 section 6.
 *
 *    localStorage persists across tabs and across sessions, so the visitor's
 *    browser would hold an id that follows them between visits. That is a
 *    cross-session profile of somebody who never signed in, sitting on our
 *    customer's own origin, and it is exactly what a buyer asks about.
 *
 *    sessionStorage survives a refresh, which is what "as long as the tab is
 *    open" means to a user, and dies with the tab. A signed-in user loses
 *    nothing: their continuity comes from the session token, not from storage.
 *
 *    The split is the product's answer to "what are you keeping about my
 *    users?": nothing that follows them around. Their conversation lives in the
 *    tab; the RECORD of what the assistant did belongs to the site owner and is
 *    in their dashboard. Switching this back for "better UX" is choosing to
 *    start tracking people.
 */
export function anonRef(): string {
  const KEY = "verb_anon_ref";
  try {
    let ref = sessionStorage.getItem(KEY);
    if (!ref) {
      ref = crypto.randomUUID();
      sessionStorage.setItem(KEY, ref); // first-party, on THEIR origin, this tab only
    }
    return ref;
  } catch {
    // Safari private mode and some CSP/storage configurations throw. History
    // just does not survive a refresh; the assistant still works.
    return `anon_${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * Clears anything this tab left behind.
 *
 * Called when the visitor signs out of the host application, so the next person
 * at a shared machine does not resume their conversation.
 */
export function forgetLocalHistory(): void {
  try {
    sessionStorage.removeItem("verb_anon_ref");
    // A visitor upgrading from an older build still has the long-lived id in
    // localStorage, and it must not outlive this release. Shipping the fix
    // without clearing it would leave exactly the profile it exists to remove.
    localStorage.removeItem("verb_anon_ref");
    // 🔴 The transcript goes too, and it is the whole point of this function.
    //    Clearing the id while leaving the conversation on screen would hand
    //    the next person at a shared machine the previous person's questions.
    for (const k of Object.keys(sessionStorage)) {
      if (k.startsWith(CHAT_PREFIX)) sessionStorage.removeItem(k);
    }
  } catch {
    // Storage unavailable means there was nothing to clear.
  }
}

// ── The transcript, across a refresh ────────────────────────────────────────

const CHAT_PREFIX = "verb_chat_";

/**
 * One line of a saved conversation. Deliberately not the DOM: replaying
 * markup we once rendered would mean trusting stored strings as markup, and
 * the panel already knows how to render a role and some text safely.
 */
export type SavedEntry =
  | {
      k: "m";
      role: "user" | "assistant";
      text: string;
      /**
       * The server's message id, on an assistant row that was actually
       * persisted. Its absence is meaningful: a written starter answer and a
       * turn that died before it was saved both have none, and neither may be
       * offered a thumb that would post an id resolving to nothing.
       */
      id?: string;
      /** A rating this visitor already submitted. docs/29 section 4. */
      fb?: "up" | "down";
      /**
       * When it was said, as epoch milliseconds (one UTC instant). Shown in the visitor's own
       * timezone by time.ts and never stored as words, so it is the same moment on every device.
       * Absent on a transcript saved before times existed, which then shows none.
       */
      at?: number;
    }
  /**
   * `calls` carries each call's label AND its outcome, so a restored group
   * still says "List banners" rather than "Tool call".
   *
   * 🔴 Added after the first version stored only a count. Reloading turned a
   *    perfectly good audit line into two rows reading "Tool call", which is
   *    strictly worse than showing nothing: the panel claims to tell you what
   *    it did while withholding the only part that matters.
   *
   * 🔴 NAME AND STATUS TOGETHER, IN ONE OBJECT, NOT TWO PARALLEL ARRAYS. The
   *    first attempt stored `names` beside the existing `failed` COUNT, and
   *    restore rebuilt statuses by marking the first `failed` chips as errors.
   *    That silently mismatched them: with one failure out of two, the tick
   *    and the cross landed on the wrong labels. A test caught it. Pairing the
   *    two makes the mistake unrepresentable rather than merely fixed.
   *
   * `n` and `failed` stay for transcripts written before this existed.
   */
  | {
      k: "t";
      n: number;
      failed: number;
      calls?: { name: string; status: string }[];
      /**
       * Seconds the turn took, so a restored summary still says how long the assistant
       * worked ("Show details · 2 tools · 3.4s"). Lost on every reload until now.
       */
      elapsed?: number;
      /**
       * The approvals made during the turn: what the visitor was asked, and what they
       * answered. This is the "details" in "Show details".
       *
       * 🔴 THESE ARE THE EVIDENCE OF WHAT THEY APPROVED, AND THEY VANISHED ON REFRESH.
       *    A resolved card is folded into the tool group precisely so the record of an
       *    approval is one press away, and the snapshot never saved it, so reloading kept
       *    the words and dropped the proof of what had been done in them. Safe to keep
       *    since the card shows rows built for a person (docs/56, phase 5), not arguments.
       */
      cards?: SavedCard[];
    };

/** One resolved approval, as the visitor saw it. Rows are the words on the card, nothing behind them. */
export interface SavedCard {
  title: string;
  rows: { label: string; value: string }[];
  /** The word that was on the card: "Confirmed", "Cancelled" or "Sent". */
  outcome: string;
  decision: "allow" | "deny";
  warning?: string;
}

const MAX_CARDS = 10;
const MAX_ROWS = 20;
const MAX_TEXT = 300;

export interface SavedChat {
  /** So the server resumes the same conversation, not just the same-looking one. */
  conversationId?: string;
  entries: SavedEntry[];
}

/**
 * Keeps the conversation on screen across a reload, for as long as the tab
 * lives.
 *
 * 🔴 sessionStorage, for exactly the reasons spelled out on anonRef above, and
 *    this is the storage that makes those reasons matter most: it holds what
 *    the visitor actually TYPED, not just an opaque id. localStorage here
 *    would leave somebody's questions sitting on our customer's origin after
 *    they closed the tab, readable by any script on that origin, which is a
 *    different product from the one we sell. It dies with the tab, which is
 *    also precisely what Vishnu asked for: intact across a refresh, gone when
 *    the browser closes.
 *
 * 🔴 Keyed per SITE KEY. Two Verb widgets on one origin, a live one and a test
 *    one being compared side by side, must not read each other's history back.
 *
 * Refusing to write is never an error worth surfacing: Safari private mode and
 * some CSP configurations throw, and a quota can be full. The conversation
 * still works, it just stops surviving reloads.
 */
export function saveChat(siteKey: string, chat: SavedChat): void {
  try {
    // Bounded twice: by turns, and by characters, because one pasted stack
    // trace can be larger than the whole rest of the history. The tail is what
    // is kept; the oldest exchange is the one nobody scrolls back for.
    const entries = chat.entries.slice(-MAX_SAVED_ENTRIES);
    let json = JSON.stringify({ ...chat, entries });
    while (json.length > MAX_SAVED_CHARS && entries.length > 2) {
      entries.splice(0, 2);
      json = JSON.stringify({ ...chat, entries });
    }
    sessionStorage.setItem(CHAT_PREFIX + siteKey, json);
  } catch {
    // No persistence available. The panel is unaffected.
  }
}

const MAX_SAVED_ENTRIES = 60;
const MAX_SAVED_CHARS = 100_000;

/**
 * Forgets THIS site's saved conversation, and nothing else.
 *
 * Deliberately narrower than forgetLocalHistory, which also drops the anon ref
 * and every other site's transcript because it exists for a sign-out. Starting
 * a new chat is not a sign-out: the same person is still here and still the
 * same visitor, they are only done with this thread.
 */
export function clearChat(siteKey: string): void {
  try {
    sessionStorage.removeItem(CHAT_PREFIX + siteKey);
  } catch {
    // Nothing was persisted, so there is nothing to clear.
  }
}

/**
 * One entry, checked rather than trusted.
 *
 * 🔴 Its own function because writing this inline got the precedence wrong:
 *    `a && b && (c) || (d)` parses as `(a && b && c) || d`, so the null guard
 *    at the front did not cover the second branch and a stored `null` threw a
 *    TypeError reading `.k` off it. That was caught by the test below rather
 *    than in production, where the effect would have been the whole saved
 *    conversation silently discarded on load.
 */
function isSavedEntry(e: unknown): e is SavedEntry {
  if (!e || typeof e !== "object") return false;
  const v = e as { k?: unknown; role?: unknown; text?: unknown; n?: unknown };
  if (v.k === "m") {
    if (typeof v.text !== "string" || (v.role !== "user" && v.role !== "assistant")) return false;
    /**
     * 🔴 THE ID AND THE RATING ARE CHECKED, NOT TRUSTED, like everything else
     *    out of this store. They came off an origin that is not ours, where any
     *    script on the page could have written them, and `id` is posted
     *    straight back to /v1/feedback. A malformed one is dropped rather than
     *    failing the whole entry: losing a thumb beats losing the message.
     */
    const m = e as { id?: unknown; fb?: unknown; at?: unknown };
    if (m.id !== undefined && typeof m.id !== "string") delete m.id;
    if (m.fb !== undefined && m.fb !== "up" && m.fb !== "down") delete m.fb;
    // Checked like everything else out of this store: a number that is not a believable moment is dropped.
    if (m.at !== undefined && !isPlausibleTime(m.at)) delete m.at;
    return true;
  }
  if (v.k !== "t" || typeof v.n !== "number") return false;
  // Labels are decoration, so a malformed list is dropped rather than failing
  // the whole entry: losing a label beats losing the turn.
  const calls = (e as { calls?: unknown }).calls;
  const wellFormed =
    Array.isArray(calls) &&
    calls.every(
      (c) =>
        !!c &&
        typeof c === "object" &&
        typeof (c as { name?: unknown }).name === "string" &&
        typeof (c as { status?: unknown }).status === "string",
    );
  if (calls !== undefined && !wellFormed) delete (e as { calls?: unknown }).calls;

  // The same rule for the two newer fields: a malformed one is dropped, never the whole turn.
  const t = e as { elapsed?: unknown; cards?: unknown };
  if (t.elapsed !== undefined && !(typeof t.elapsed === "number" && Number.isFinite(t.elapsed) && t.elapsed >= 0)) {
    delete t.elapsed;
  }
  if (t.cards !== undefined) {
    const clean = Array.isArray(t.cards) ? t.cards.slice(0, MAX_CARDS).map(cleanCard) : null;
    if (clean && clean.every((c): c is SavedCard => c !== null)) t.cards = clean;
    else delete t.cards;
  }
  return true;
}

/** One saved approval, checked and bounded, or null if it is not one of ours. */
function cleanCard(c: unknown): SavedCard | null {
  if (!c || typeof c !== "object") return null;
  const v = c as Record<string, unknown>;
  if (typeof v["title"] !== "string" || typeof v["outcome"] !== "string") return null;
  if (v["decision"] !== "allow" && v["decision"] !== "deny") return null;
  if (!Array.isArray(v["rows"])) return null;
  const rows: { label: string; value: string }[] = [];
  for (const r of (v["rows"] as unknown[]).slice(0, MAX_ROWS)) {
    const row = r as { label?: unknown; value?: unknown } | null;
    if (!row || typeof row.label !== "string" || typeof row.value !== "string") return null;
    rows.push({ label: row.label.slice(0, MAX_TEXT), value: row.value.slice(0, MAX_TEXT) });
  }
  return {
    title: v["title"].slice(0, MAX_TEXT),
    rows,
    outcome: v["outcome"].slice(0, 40),
    decision: v["decision"],
    ...(typeof v["warning"] === "string" ? { warning: v["warning"].slice(0, MAX_TEXT) } : {}),
  };
}

/** What was on screen last time, or nothing. Never throws. */
export function loadChat(siteKey: string): SavedChat | null {
  try {
    const raw = sessionStorage.getItem(CHAT_PREFIX + siteKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const entries = (parsed as SavedChat).entries;
    if (!Array.isArray(entries)) return null;
    // Filtered rather than trusted. This came out of storage on someone else's
    // origin, where any script on the page could have written it, so a shape
    // that is not ours is discarded instead of rendered.
    const clean = entries.filter(isSavedEntry);
    const conversationId = (parsed as SavedChat).conversationId;
    return {
      ...(typeof conversationId === "string" ? { conversationId } : {}),
      entries: clean,
    };
  } catch {
    return null;
  }
}

/**
 * The identified visitor's session token, cached across a reload.
 *
 * 🔴 CONFIRMED ON MIRCHI35: a signed-in admin hit a hard reload mid-session and
 *    the panel came back anonymous, refusing the exact write it had allowed a
 *    minute earlier. The token only ever lived in a JS variable, so a reload
 *    lost it and the panel stayed anonymous until the host page's own
 *    `Verb.identify()` call fired again, which is not instant and was not
 *    guaranteed to happen at all.
 *
 *    This was a deliberate choice, not an oversight: `index.ts` used to treat
 *    the token as OPAQUE and never persist it, on the same instinct as
 *    `anonRef` above, keep as little about the visitor as possible. But that
 *    reasoning fits an id WE invent, not a credential their OWN backend
 *    already handed to their OWN page's JS. Caching it does not create a new
 *    profile: sessionStorage dies with the tab exactly like anonRef, and this
 *    additionally respects the token's own signed `exp`, so it never outlives
 *    what the customer's backend already decided. Anything on the page
 *    capable of reading this key could equally well have monkey-patched
 *    `window.Verb.identify` to read the token as it arrived, so this is not a
 *    materially larger exposure than keeping it in memory, just one that
 *    survives a refresh.
 *
 * Namespaced by site key so a page that ever embeds two sites (staging next to
 * production, say) cannot hand one site's token to the other. The backend
 * would reject a mismatched token anyway (`verifySessionToken` checks the
 * signature against that site's own key), so a collision fails closed, but
 * there is no reason to invite the spurious 401.
 */
export function storedSessionToken(siteKey: string): string | null {
  const key = `verb_session_token:${siteKey}`;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;

    const exp = tokenExpiry(raw);
    if (exp === null || exp <= Date.now() / 1000) {
      sessionStorage.removeItem(key);
      return null;
    }
    return raw;
  } catch {
    return null; // Private mode or storage disabled: start anonymous, same as before.
  }
}

export function persistSessionToken(siteKey: string, token: string): void {
  try {
    sessionStorage.setItem(`verb_session_token:${siteKey}`, token);
  } catch {
    // Storage unavailable. The token still works for this page load, it just
    // will not survive a reload, which is exactly today's behaviour.
  }
}

/**
 * Drops the cached identity for this site.
 *
 * 🔴 THIS DID NOT EXIST UNTIL 2026-09-04, AND ITS ABSENCE WAS HALF OF A REAL
 *    LEAK. `refreshIdentity` handled an explicit sign-out by setting the token
 *    variable to undefined and stopping there, so the credential stayed in
 *    sessionStorage and the next reload restored it. Signing out of the host
 *    app did not sign you out of the assistant.
 */
export function forgetSessionToken(siteKey: string): void {
  try {
    sessionStorage.removeItem(`verb_session_token:${siteKey}`);
  } catch {
    // Nothing stored, so nothing to forget.
  }
}

/**
 * Who a token says it is for, WITHOUT verifying the signature.
 *
 * 🔴 THE WIDGET STILL TREATS THIS TOKEN AS OPAQUE IN THE SENSE THAT MATTERS.
 *    Whether this claim is TRUE is decided by the server on every single turn,
 *    against that site's own signing key. This reads it for one local purpose:
 *    deciding whether the person in front of the screen changed, and therefore
 *    whether the conversation on that screen belongs to somebody else. Exactly
 *    the same argument as `tokenExpiry` above, which has read `exp` since the
 *    day tokens were cached.
 *
 *    A forged `sub` buys an attacker nothing here: the worst it can do is clear
 *    a transcript, and clearing is the safe direction.
 */
export function tokenSubject(token: string | null | undefined): string | null {
  if (!token) return null;
  try {
    const [body] = token.split(".");
    if (!body) return null;
    const padded = body.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(body.length / 4) * 4, "=");
    const claims = JSON.parse(atob(padded)) as { sub?: unknown };
    return typeof claims.sub === "string" && claims.sub ? claims.sub : null;
  } catch {
    return null;
  }
}

/**
 * Reads `exp` without verifying the signature: verification is the server's
 * job, on every turn, regardless of what this returns. This only decides
 * whether a stored token is worth trying, never whether it is trusted.
 *
 * 🔴 EXPORTED SO A TURN CAN REFRESH BEFORE IT SENDS, not only after it fails.
 *    Tokens are minted with a 15 minute life, so any tab left open longer than
 *    that had its next question refused. Recovering from the 401 is necessary
 *    and not sufficient: the visitor still waits for a round trip that was
 *    always going to fail. Checking here means they usually never find out.
 */
export function tokenExpiry(token: string): number | null {
  try {
    const [body] = token.split(".");
    if (!body) return null;
    const padded = body.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(body.length / 4) * 4, "=");
    const claims = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof claims.exp === "number" ? claims.exp : null;
  } catch {
    return null;
  }
}
